// Lightweight canvas particle effects for festival greetings.
// Each effect is a factory: (width, height, opts) => { step(ctx, dt, w, h) }.
// Particle counts are capped so this stays smooth on low-end phones.

const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Pre-rendered soft glow dot, reused via drawImage (much cheaper than per-frame gradients).
const spriteCache = new Map();
function glowSprite(color, size = 64) {
  const key = `${color}-${size}`;
  if (spriteCache.has(key)) return spriteCache.get(key);
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, color);
  grad.addColorStop(0.4, color);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  spriteCache.set(key, c);
  return c;
}

// Fade the previous frame instead of clearing it, leaving short trails.
function fadeFrame(ctx, w, h, amount) {
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = `rgba(0,0,0,${amount})`;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
}

// ---- Diwali: fireworks -----------------------------------------------------

function fireworks() {
  const colors = ['#ffd166', '#ff6b6b', '#f72585', '#4cc9f0', '#80ffdb', '#ff9f1c', '#ffffff', '#c77dff'];
  const rockets = [];
  const sparks = [];
  let timer = 0;

  return {
    step(ctx, dt, w, h) {
      fadeFrame(ctx, w, h, 0.18);

      timer -= dt;
      if (timer <= 0 && rockets.length < 6) {
        rockets.push({ x: rand(w * 0.15, w * 0.85), y: h, vy: -rand(h * 0.9, h * 1.25), targetY: rand(h * 0.12, h * 0.45), color: pick(colors) });
        timer = rand(0.25, 0.6);
      }

      ctx.globalCompositeOperation = 'lighter';
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.y += r.vy * dt;
        r.vy += h * 0.35 * dt;
        ctx.drawImage(glowSprite('#ffe8a3', 24), r.x - 6, r.y - 6, 12, 12);
        if (r.y <= r.targetY || r.vy >= 0) {
          const count = Math.min(70, 400 - sparks.length);
          const speed = rand(Math.min(w, h) * 0.18, Math.min(w, h) * 0.32);
          const color2 = Math.random() < 0.4 ? pick(colors) : r.color;
          for (let j = 0; j < count; j++) {
            const a = (j / count) * Math.PI * 2 + rand(-0.05, 0.05);
            const s = speed * rand(0.6, 1);
            sparks.push({ x: r.x, y: r.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rand(0.9, 1.6), age: 0, color: j % 2 ? r.color : color2 });
          }
          rockets.splice(i, 1);
        }
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.age += dt;
        if (p.age >= p.life) { sparks.splice(i, 1); continue; }
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + h * 0.12 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        const alpha = 1 - p.age / p.life;
        ctx.globalAlpha = alpha;
        const size = 3 + 5 * alpha;
        ctx.drawImage(glowSprite(p.color, 32), p.x - size, p.y - size, size * 2, size * 2);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    },
  };
}

// ---- Holi: colour powder bursts -----------------------------------------------

function colors() {
  const palette = ['rgba(236,72,153,0.9)', 'rgba(250,204,21,0.9)', 'rgba(34,197,94,0.9)', 'rgba(59,130,246,0.9)', 'rgba(249,115,22,0.9)', 'rgba(168,85,247,0.9)', 'rgba(6,182,212,0.9)'];
  const puffs = [];
  let timer = 0;

  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);

      timer -= dt;
      if (timer <= 0 && puffs.length < 260) {
        const cx = rand(w * 0.05, w * 0.95);
        const cy = rand(h * 0.1, h * 0.9);
        const color = pick(palette);
        const n = 28;
        for (let i = 0; i < n; i++) {
          const a = rand(0, Math.PI * 2);
          const s = rand(40, 260);
          puffs.push({ x: cx, y: cy, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: rand(18, 46), grow: rand(30, 70), life: rand(1.6, 2.8), age: 0, color });
        }
        timer = rand(0.18, 0.4);
      }

      for (let i = puffs.length - 1; i >= 0; i--) {
        const p = puffs[i];
        p.age += dt;
        if (p.age >= p.life) { puffs.splice(i, 1); continue; }
        p.vx *= 0.94;
        p.vy = p.vy * 0.94 + 18 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.r += p.grow * dt;
        const t = p.age / p.life;
        ctx.globalAlpha = 0.55 * (1 - t) * Math.min(1, p.age * 6);
        ctx.drawImage(glowSprite(p.color, 64), p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
      }
      ctx.globalAlpha = 1;
    },
  };
}

// ---- Christmas: snowfall + twinkling lights ------------------------------------

function snow(w, h) {
  const flakes = Array.from({ length: Math.min(180, Math.round((w * h) / 9000)) }, () => ({
    x: rand(0, w), y: rand(-h, h), r: rand(1.5, 4.5), vy: rand(30, 90), phase: rand(0, Math.PI * 2), sway: rand(10, 35),
  }));
  const bulbColors = ['#ef4444', '#facc15', '#22c55e', '#3b82f6', '#f97316'];
  let t = 0;

  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);
      t += dt;

      // String of lights across the top
      const bulbs = Math.max(8, Math.round(w / 70));
      ctx.strokeStyle = 'rgba(30,41,59,0.8)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i <= bulbs; i++) {
        const x = (i / bulbs) * w;
        const y = 22 + Math.sin((i / bulbs) * Math.PI * 4) * 10;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      for (let i = 0; i <= bulbs; i++) {
        const x = (i / bulbs) * w;
        const y = 30 + Math.sin((i / bulbs) * Math.PI * 4) * 10;
        const on = 0.35 + 0.65 * Math.max(0, Math.sin(t * 3 + i * 1.7));
        ctx.globalAlpha = on;
        ctx.drawImage(glowSprite(bulbColors[i % bulbColors.length], 48), x - 14, y - 14, 28, 28);
      }
      ctx.globalAlpha = 1;

      const flake = glowSprite('rgba(255,255,255,0.95)', 32);
      for (const f of flakes) {
        f.y += f.vy * dt;
        f.phase += dt;
        const x = f.x + Math.sin(f.phase) * f.sway;
        if (f.y > h + 10) { f.y = -10; f.x = rand(0, w); }
        const s = f.r * 2.4;
        ctx.drawImage(flake, x - s / 2, f.y - s / 2, s, s);
      }
    },
  };
}

// ---- Makar Sankranti: flying kites --------------------------------------------

function kites(w, h) {
  const pairs = [['#ef4444', '#facc15'], ['#3b82f6', '#f97316'], ['#22c55e', '#ec4899'], ['#a855f7', '#facc15'], ['#06b6d4', '#ef4444'], ['#f97316', '#16a34a']];
  const count = w < 640 ? 4 : 7;
  const list = Array.from({ length: count }, (_, i) => ({
    x: rand(0, w), y: rand(h * 0.08, h * 0.55), size: rand(28, 52), vx: rand(20, 55) * (Math.random() < 0.5 ? -1 : 1),
    phase: rand(0, Math.PI * 2), colors: pairs[i % pairs.length], anchorX: rand(w * 0.1, w * 0.9),
  }));
  let t = 0;

  function drawKite(ctx, k, tilt) {
    const s = k.size;
    ctx.save();
    ctx.translate(k.x, k.y);
    ctx.rotate(tilt);
    // two-tone diamond
    ctx.fillStyle = k.colors[0];
    ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.75, 0); ctx.lineTo(0, s * 1.1); ctx.closePath(); ctx.fill();
    ctx.fillStyle = k.colors[1];
    ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(-s * 0.75, 0); ctx.lineTo(0, s * 1.1); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(0, s * 1.1); ctx.moveTo(-s * 0.75, 0); ctx.lineTo(s * 0.75, 0); ctx.stroke();
    // wavy tail
    ctx.strokeStyle = k.colors[0];
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(0, s * 1.1);
    for (let i = 1; i <= 6; i++) ctx.lineTo(Math.sin(t * 5 + i + k.phase) * 7, s * 1.1 + i * 9);
    ctx.stroke();
    ctx.restore();
  }

  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);
      t += dt;
      for (const k of list) {
        k.x += k.vx * dt;
        if (k.x > w + 80) k.x = -80;
        if (k.x < -80) k.x = w + 80;
        const y = k.y + Math.sin(t * 1.3 + k.phase) * 18;
        const tilt = Math.sin(t * 1.8 + k.phase) * 0.25;
        // string down to the "flyer"
        ctx.strokeStyle = 'rgba(255,255,255,0.35)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(k.x, y + k.size * 1.1);
        ctx.quadraticCurveTo((k.x + k.anchorX) / 2, h * 0.85, k.anchorX, h + 10);
        ctx.stroke();
        drawKite(ctx, { ...k, y }, tilt);
      }
    },
  };
}

// ---- New Year: confetti ----------------------------------------------------------

function confetti(w, h, opts = {}) {
  const palette = opts.palette || ['#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#facc15', '#ffffff'];
  const pieces = [];
  const make = (x, y, vx, vy) => ({ x, y, vx, vy, w: rand(6, 11), h: rand(10, 18), rot: rand(0, Math.PI), vr: rand(-6, 6), flip: rand(0, Math.PI * 2), vf: rand(4, 10), color: pick(palette) });

  // Opening burst from both bottom corners
  for (let i = 0; i < 90; i++) {
    pieces.push(make(0, h, rand(200, 600), -rand(500, 950)));
    pieces.push(make(w, h, -rand(200, 600), -rand(500, 950)));
  }

  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);
      if (pieces.length < 260) pieces.push(make(rand(0, w), -20, rand(-40, 40), rand(60, 140)));

      for (let i = pieces.length - 1; i >= 0; i--) {
        const p = pieces[i];
        p.vx *= 0.99;
        p.vy = Math.min(p.vy + 600 * dt, 220);
        p.x += p.vx * dt + Math.sin(p.flip) * 0.6;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.flip += p.vf * dt;
        if (p.y > h + 30) { pieces.splice(i, 1); continue; }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, Math.cos(p.flip)); // 3D-ish flip
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
    },
  };
}

// ---- Republic / Independence Day: tricolour confetti + flowing ribbons ------------

function tricolor(w, h) {
  const base = confetti(w, h, { palette: ['#ff9933', '#ff9933', '#ffffff', '#138808', '#138808', '#000080'] });
  const bands = ['#ff9933', '#ffffff', '#138808'];
  let t = 0;
  return {
    step(ctx, dt, w, h) {
      base.step(ctx, dt, w, h); // clears the frame
      t += dt;
      // three waving ribbons across the top, like a flag in the wind
      const bh = Math.max(10, h * 0.018);
      bands.forEach((color, i) => {
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 16) {
          const y = i * bh + Math.sin(x / 90 + t * 3) * 6;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        for (let x = w; x >= 0; x -= 16) ctx.lineTo(x, (i + 1) * bh + Math.sin(x / 90 + t * 3) * 6);
        ctx.closePath();
        ctx.fill();
      });
      ctx.globalAlpha = 1;
    },
  };
}

// ---- Flower petals (Raksha Bandhan, Janmashtami, Ganesh, Navratri) -------------------

export function drawPetal(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(1, Math.abs(Math.cos(p.flip)) * 0.7 + 0.3);
  ctx.fillStyle = p.color;
  ctx.beginPath();
  ctx.ellipse(0, 0, p.size, p.size * 0.55, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function petals(w, h, opts = {}) {
  const palette = opts.palette || ['#f97316', '#facc15', '#ef4444'];
  const make = (y) => ({ x: rand(0, w), y, vy: rand(50, 120), sway: rand(20, 60), phase: rand(0, Math.PI * 2), size: rand(6, 12), rot: rand(0, Math.PI), vr: rand(-2, 2), flip: rand(0, Math.PI), vf: rand(2, 5), color: pick(palette) });
  const list = Array.from({ length: Math.min(160, Math.round((w * h) / 9000)) }, () => make(rand(-h, 0)));
  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);
      for (const p of list) {
        p.y += p.vy * dt;
        p.phase += dt;
        p.rot += p.vr * dt;
        p.flip += p.vf * dt;
        if (p.y > h + 20) Object.assign(p, make(-20));
        drawPetal(ctx, { ...p, x: p.x + Math.sin(p.phase) * p.sway });
      }
    },
  };
}

// ---- Rising lights / lanterns (Shivaratri, Guru Nanak Jayanti) -------------------

function lights(w, h, opts = {}) {
  const palette = opts.palette || ['#fbbf24', '#f59e0b', '#fde68a'];
  const make = (y) => ({ x: rand(0, w), y, vy: -rand(30, 80), size: rand(8, 22), phase: rand(0, Math.PI * 2), color: pick(palette) });
  const list = Array.from({ length: Math.min(90, Math.round((w * h) / 16000)) }, () => make(rand(0, h * 1.5)));
  let t = 0;
  return {
    step(ctx, dt, w, h) {
      ctx.clearRect(0, 0, w, h);
      t += dt;
      ctx.globalCompositeOperation = 'lighter';
      for (const p of list) {
        p.y += p.vy * dt;
        if (p.y < -30) Object.assign(p, make(h + 30));
        const flicker = 0.65 + 0.35 * Math.sin(t * 4 + p.phase);
        const x = p.x + Math.sin(t + p.phase) * 15;
        ctx.globalAlpha = flicker;
        ctx.drawImage(glowSprite(p.color, 64), x - p.size, p.y - p.size, p.size * 2, p.size * 2);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    },
  };
}

export const EFFECTS = { fireworks, colors, snow, kites, confetti, tricolor, petals, lights };
export { glowSprite, rand, pick };

// Runs an effect on a canvas until the returned stop() is called.
export function runEffect(canvas, effectName, opts = {}) {
  const factory = EFFECTS[effectName];
  if (!factory || !canvas) return () => {};
  const ctx = canvas.getContext('2d');
  let w = 0;
  let h = 0;

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener('resize', resize);

  const effect = factory(w, h, opts);
  let last = performance.now();
  let raf = requestAnimationFrame(function frame(now) {
    const dt = Math.min((now - last) / 1000, 0.05); // clamp after tab switches
    last = now;
    effect.step(ctx, dt, w, h);
    raf = requestAnimationFrame(frame);
  });

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}
